import PropTypes from "prop-types";
import {useEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import styles from "./basemodal.module.css";

export const BaseModal = ({
  open,
  state,
  openModal,
  onCancel,
  onClose,
  closeModal,
  onOk,
  cancelCallback,
  submitCallback,
  submitTitle = "Confirmar",
  cancelTitle = "Cancelar",
  title,
  width = 520,
  className = "",
  wrapClassName = "",
  closable = true,
  destroyOnHidden = false,
  maskClosable = true,
  closeOnBackdrop = true,
  keyboard = true,
  closeOnEsc = true,
  styles: customStyles = {},
  style = {},
  customStyles: oldCustomStyles = {},
  zIndex = 1000,
  centered = true,
  footer,
  children,
}) => {
  const isOpen = open ?? state ?? openModal ?? false;
  const handleClose = onCancel ?? onClose ?? closeModal ?? onOk ?? cancelCallback;
  const isMaskClosable = maskClosable && closeOnBackdrop;
  const isEscClosable = keyboard && closeOnEsc;

  const wrapRef = useRef(null);
  const [hasOpened, setHasOpened] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setHasOpened(true);
    }
  }, [isOpen]);

  // Lock scroll on open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      const activeWraps = document.querySelectorAll(".ant-modal-wrap");
      const otherActive = Array.from(activeWraps).some(wrap => {
        return wrap !== wrapRef.current && !wrap.className.includes("Hidden") && !wrap.className.includes("hidden");
      });
      if (!otherActive) {
        document.body.style.overflow = "";
      }
    }

    return () => {
      const activeWraps = document.querySelectorAll(".ant-modal-wrap");
      const otherActive = Array.from(activeWraps).some(wrap => {
        return wrap !== wrapRef.current && !wrap.className.includes("Hidden") && !wrap.className.includes("hidden");
      });
      if (!otherActive) {
        document.body.style.overflow = "";
      }
    };
  }, [isOpen]);

  // ESC key listener
  useEffect(() => {
    if (!isOpen || !isEscClosable) return;

    const handleKeyDown = event => {
      if (event.key === "Escape") {
        handleClose?.();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, isEscClosable, handleClose]);

  const handleMaskClick = event => {
    if (isMaskClosable && event.target === event.currentTarget) {
      handleClose?.();
    }
  };

  const handleModalClick = event => {
    event.stopPropagation();
  };

  const shouldRender = isOpen || (hasOpened && !destroyOnHidden);
  if (!shouldRender || typeof document === "undefined") return null;

  const modalWidth = typeof width === "number" ? `${width}px` : width;
  const hasActionButtons = submitCallback || cancelCallback;
  const showFooter = (footer !== undefined && footer !== null && footer !== false) || hasActionButtons;

  // Merge style props
  const mergedModalStyles = {
    width: modalWidth,
    ...style,
    ...oldCustomStyles,
    ...customStyles.modal,
  };

  return createPortal(
    <div
      className={`${styles.root} ant-modal-root`}
      style={{zIndex}}
    >
      {/* Mask Backdrop overlay */}
      <div
        className={`${styles.mask} ${isOpen ? styles.maskVisible : styles.maskHidden} ant-modal-mask`}
        style={customStyles.mask}
        onClick={handleMaskClick}
      />

      {/* Wrapper alignment layer */}
      <div
        ref={wrapRef}
        className={`${styles.wrap} ${
          isOpen ? styles.wrapVisible : styles.wrapHidden
        } ${centered ? `${styles.centered} ant-modal-centered` : ""} ${wrapClassName} ant-modal-wrap`}
        style={customStyles.wrapper}
        onClick={handleMaskClick}
      >
        {/* Modal content box */}
        <div
          className={`${styles.modal} ${isOpen ? styles.modalVisible : styles.modalHidden} ${className} ant-modal`}
          style={mergedModalStyles}
          onClick={handleModalClick}
        >
          <div
            className={`${styles.content} ant-modal-content`}
            style={customStyles.content}
          >
            {closable && (
              <button
                type="button"
                className={`${styles.closeButton} ant-modal-close`}
                onClick={handleClose}
                aria-label="Close"
              >
                <span className="ant-modal-close-x">
                  <svg
                    viewBox="64 64 896 896"
                    focusable="false"
                    data-icon="close"
                    width="1em"
                    height="1em"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M563.8 512l262.5-312.9c4.4-5.2.7-13.1-6.1-13.1h-79.8c-4.7 0-9.2 2.1-12.3 5.7L511.6 449.8 295.1 191.7c-3-3.6-7.5-5.7-12.3-5.7H203c-6.8 0-10.5 7.9-6.1 13.1L459.4 512 196.9 824.9A7.95 7.95 0 00203 838h79.8c4.7 0 9.2-2.1 12.3-5.7l216.5-258.1 216.5 258.1c3 3.6 7.5 5.7 12.3 5.7h79.8c6.8 0 10.5-7.9 6.1-13.1L563.8 512z"></path>
                  </svg>
                </span>
              </button>
            )}

            {title && (
              <div
                className={`${styles.header} ant-modal-header`}
                style={customStyles.header}
              >
                <div className={`${styles.title} ant-modal-title`}>{title}</div>
              </div>
            )}

            <div
              className={`${styles.body} ant-modal-body`}
              style={customStyles.body}
            >
              {children}
            </div>

            {showFooter && (
              <div
                className={`${styles.footer} ant-modal-footer`}
                style={customStyles.footer}
              >
                {footer ? (
                  footer
                ) : (
                  <>
                    {cancelCallback && (
                      <button
                        className={`${styles.modalButton} ${styles.cancelButton}`}
                        onClick={handleClose}
                        type="button"
                      >
                        {cancelTitle}
                      </button>
                    )}
                    {submitCallback && (
                      <button
                        className={`${styles.modalButton} ${styles.submitButton}`}
                        onClick={submitCallback}
                        type="button"
                      >
                        {submitTitle}
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

BaseModal.propTypes = {
  open: PropTypes.bool,
  state: PropTypes.bool,
  openModal: PropTypes.bool,
  onCancel: PropTypes.func,
  onClose: PropTypes.func,
  closeModal: PropTypes.func,
  onOk: PropTypes.func,
  cancelCallback: PropTypes.func,
  submitCallback: PropTypes.func,
  submitTitle: PropTypes.string,
  cancelTitle: PropTypes.string,
  title: PropTypes.node,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
  wrapClassName: PropTypes.string,
  closable: PropTypes.bool,
  destroyOnHidden: PropTypes.bool,
  maskClosable: PropTypes.bool,
  closeOnBackdrop: PropTypes.bool,
  keyboard: PropTypes.bool,
  closeOnEsc: PropTypes.bool,
  styles: PropTypes.object,
  style: PropTypes.object,
  customStyles: PropTypes.object,
  zIndex: PropTypes.number,
  centered: PropTypes.bool,
  footer: PropTypes.node,
  children: PropTypes.node,
};

export default BaseModal;
